import HttpFactory from '~/repository/factory';

export interface AquarioOption {
  id: string;
  nome: string;
  id_corretagem: number;
  ativo: boolean;
  previdencia_ativa?: boolean;
  /**
   * Prefixo do código de produto Vida (`{grupo}_{nivel_ativo}`). Usado pela
   * Simulação Rápida (UC087) para montar o codigoProduto da cotação. Pode ser
   * null quando o aquário não tem produto Vida mapeado.
   */
  codigo_produto_vida?: string | null;
}

/** Nível de um aquário, conforme retornado pela listagem administrativa. */
export interface AdminAquarioNivel {
  id: string;
  aquario_id: string;
  nivel: number;
  id_corretagem: number;
  id_pro_labore: number;
  id_agenciamento: number;
  percentual_corretagem?: number | string | null;
}

/**
 * Campos da dimensão Previdência (D11). Quando `previdencia_ativa` é `true`,
 * os 4 campos de produtor/código são obrigatórios (validado no form e no backend).
 */
export interface PrevidenciaFields {
  previdencia_ativa: boolean;
  previdencia_produtor_codigo: string | null;
  previdencia_codigo_ponto_atendimento: string | null;
  previdencia_codigo_unidade_produtora: string | null;
  previdencia_angariador: string | null;
}

/** Aquário no contexto administrativo, já com seus níveis hidratados. */
export interface AdminAquario extends PrevidenciaFields {
  id: string;
  nome: string;
  ativo: boolean;
  id_corretagem: number;
  /** CNPJ persistido como 14 dígitos sem máscara (D12); pode ser nulo. */
  cnpj: string | null;
  /** Número do nível ativo da dimensão Vida; nulo quando não há nível ativo. */
  nivel_ativo: number | null;
  niveis: AdminAquarioNivel[];
  created_at: string;
  updated_at: string;
}

/** Envelope paginado do `GET /admin/aquarios`. */
export interface AquariosListResult {
  data: AdminAquario[];
  total: number;
  pages: number;
}

/**
 * Nível no contexto de edição do `AquarioForm`. `nivel` e os IDs podem ser
 * `null` enquanto o usuário não preenche (o form valida antes de emitir).
 */
export interface AquarioNivelFormValue {
  nivel: number | null;
  id_corretagem: number | null;
  id_pro_labore: number | null;
  id_agenciamento: number | null;
  percentual_corretagem: number | null;
}

/** Estado completo editado/emitido pelo `AquarioForm`. */
export interface AquarioFormValue extends PrevidenciaFields {
  nome: string;
  ativo: boolean;
  /** CNPJ digitado pelo usuário (pode vir com máscara; backend normaliza). */
  cnpj: string | null;
  /** Nível ativo selecionado na aba Vida (radio "Ativo"). */
  nivel_ativo: number | null;
  niveis: AquarioNivelFormValue[];
}

/** Filtros aceitos pela listagem administrativa (`ListAquariosQueryDto`). */
export interface ListAquariosParams {
  status?: 'ativo' | 'inativo' | 'all';
  nome?: string;
  page?: number;
  limit?: number;
}

/** Campos editáveis do aquário em si (`UpdateAquarioDto`). Níveis à parte. */
export interface UpdateAquarioParams extends Partial<PrevidenciaFields> {
  nome?: string;
  ativo?: boolean;
  cnpj?: string | null;
  nivel_ativo?: number | null;
}

/** Corpo do `POST /admin/aquarios/:id/niveis` (`CreateAquarioNivelDto`). */
export interface AquarioNivelPayload {
  nivel: number;
  id_corretagem: number;
  id_pro_labore: number;
  id_agenciamento: number;
  percentual_corretagem?: number;
}

/**
 * Corpo do `PATCH /admin/aquarios/:id/niveis/:nivel` (`UpdateAquarioNivelDto`).
 * O número do nível é imutável, então não entra no corpo.
 */
export type AquarioNivelUpdatePayload = Partial<
  Omit<AquarioNivelPayload, 'nivel'>
>;

class AquariosModule extends HttpFactory {
  private RESOURCE = '/aquarios';
  private ADMIN_RESOURCE = '/admin/aquarios';

  /** Listagem pública (ativos) usada pelo fluxo de corretor. */
  async list(): Promise<AquarioOption[]> {
    return this.get<AquarioOption[]>(this.RESOURCE);
  }

  /** Cria um aquário com seus níveis em uma única transação (`@Roles('admin')`). */
  async create(payload: AquarioFormValue): Promise<AdminAquario> {
    return this.post<AdminAquario>(this.ADMIN_RESOURCE, payload);
  }

  /** Listagem administrativa com filtros/busca/paginação (`@Roles('admin')`). */
  async listAdmin(params: ListAquariosParams = {}): Promise<AquariosListResult> {
    return this.get<AquariosListResult>(this.ADMIN_RESOURCE, {
      query: params,
    });
  }

  /** Alterna o flag `ativo` do aquário (desativar/reativar). */
  async toggleAtivo(id: string): Promise<AdminAquario> {
    return this.post<AdminAquario>(`${this.ADMIN_RESOURCE}/${id}/toggle-ativo`);
  }

  /** Carrega um aquário (com níveis hidratados) para edição. */
  async getAdmin(id: string): Promise<AdminAquario> {
    return this.get<AdminAquario>(`${this.ADMIN_RESOURCE}/${id}`);
  }

  /** Atualiza nome/status do aquário (`PATCH`). Níveis são geridos à parte. */
  async update(
    id: string,
    payload: UpdateAquarioParams,
  ): Promise<AdminAquario> {
    return this.patch<AdminAquario>(`${this.ADMIN_RESOURCE}/${id}`, payload);
  }

  /** Adiciona um nível novo ao aquário (`POST :id/niveis`). */
  async addNivel(
    id: string,
    payload: AquarioNivelPayload,
  ): Promise<AdminAquario> {
    return this.post<AdminAquario>(
      `${this.ADMIN_RESOURCE}/${id}/niveis`,
      payload,
    );
  }

  /**
   * Atualiza IDs/percentual de um nível existente (`PATCH :id/niveis/:nivel`).
   * O número do nível é imutável e identifica o recurso na URL.
   */
  async updateNivel(
    id: string,
    nivel: number,
    payload: AquarioNivelUpdatePayload,
  ): Promise<AdminAquario> {
    return this.patch<AdminAquario>(
      `${this.ADMIN_RESOURCE}/${id}/niveis/${nivel}`,
      payload,
    );
  }

  /** Remove um nível do aquário (`DELETE :id/niveis/:nivel`). */
  async deleteNivel(id: string, nivel: number): Promise<void> {
    return this.delete<void>(`${this.ADMIN_RESOURCE}/${id}/niveis/${nivel}`);
  }
}

export default AquariosModule;
